(() => {
  const selector = '.buckscc-floating-box .buckscc-currency-wrapper';
  const storageKey = 'squishpop-bucks-position-v1';
  const dragThreshold = 6;
  const initialized = new WeakSet();
  const boxes = new Set();

  const viewportBounds = (box) => {
    const rect = box.getBoundingClientRect();
    return {
      width: rect.width,
      height: rect.height,
      maxX: Math.max(0, window.innerWidth - rect.width),
      maxY: Math.max(0, window.innerHeight - rect.height),
    };
  };

  const place = (box, left, top) => {
    const bounds = viewportBounds(box);
    const x = Math.min(bounds.maxX, Math.max(0, left));
    const y = Math.min(bounds.maxY, Math.max(0, top));

    box.style.setProperty('left', `${x}px`, 'important');
    box.style.setProperty('top', `${y}px`, 'important');
    box.style.setProperty('right', 'auto', 'important');
    box.style.setProperty('bottom', 'auto', 'important');

    return { x, y, bounds };
  };

  const save = (box) => {
    const rect = box.getBoundingClientRect();
    const bounds = viewportBounds(box);
    const position = {
      x: bounds.maxX ? rect.left / bounds.maxX : 0,
      y: bounds.maxY ? rect.top / bounds.maxY : 0,
    };

    try {
      window.localStorage.setItem(storageKey, JSON.stringify(position));
    } catch (_error) {
      // Storage can be unavailable in strict privacy modes; dragging still works.
    }
  };

  const restore = (box) => {
    let position;

    try {
      position = JSON.parse(window.localStorage.getItem(storageKey));
    } catch (_error) {
      return;
    }

    if (!position || !Number.isFinite(position.x) || !Number.isFinite(position.y)) return;

    const bounds = viewportBounds(box);
    place(box, position.x * bounds.maxX, position.y * bounds.maxY);
  };

  const initialize = (box) => {
    if (initialized.has(box)) return;
    initialized.add(box);
    boxes.add(box);

    box.dataset.squishpopDraggable = 'true';
    box.style.touchAction = 'none';
    box.style.cursor = 'grab';

    let pointerId = null;
    let startX = 0;
    let startY = 0;
    let startLeft = 0;
    let startTop = 0;
    let dragging = false;
    let suppressClick = false;

    box.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || pointerId !== null) return;

      const rect = box.getBoundingClientRect();
      pointerId = event.pointerId;
      startX = event.clientX;
      startY = event.clientY;
      startLeft = rect.left;
      startTop = rect.top;
      dragging = false;
    });

    box.addEventListener('pointermove', (event) => {
      if (event.pointerId !== pointerId) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      if (!dragging && Math.hypot(deltaX, deltaY) < dragThreshold) return;

      if (!dragging) {
        dragging = true;
        box.setPointerCapture?.(pointerId);
        box.style.cursor = 'grabbing';
      }

      event.preventDefault();
      place(box, startLeft + deltaX, startTop + deltaY);
    });

    const finish = (event) => {
      if (event.pointerId !== pointerId) return;

      if (dragging) {
        suppressClick = true;
        save(box);
      }

      if (box.hasPointerCapture?.(pointerId)) box.releasePointerCapture(pointerId);
      pointerId = null;
      dragging = false;
      box.style.cursor = 'grab';
    };

    box.addEventListener('pointerup', finish);
    box.addEventListener('pointercancel', finish);
    box.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        suppressClick = false;
      },
      true
    );

    requestAnimationFrame(() => restore(box));
  };

  const scan = (root = document) => {
    if (root instanceof Element && root.matches(selector)) initialize(root);
    root.querySelectorAll?.(selector).forEach(initialize);
  };

  const start = () => {
    scan();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => scan(node)));
    });

    observer.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('resize', () => {
      boxes.forEach((box) => {
        if (!box.isConnected) {
          boxes.delete(box);
          return;
        }
        restore(box);
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
